import React, { useCallback, useMemo } from 'react';

import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import DialogContentText from '@mui/material/DialogContentText';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';

import { PlateTypeByTypeName } from 'client/app/api/PlateTypesApi';
import {
  isPlateBigEnough,
  PlateMinSizeByName,
} from 'client/app/apps/cherry-picker/CherryPickApi';
import {
  PlatesByName,
  SetPlatesByName,
  useCherryPickContext,
} from 'client/app/apps/cherry-picker/CherryPickContext';
import { PlateSelection } from 'client/app/components/Parameters/PlateType/PlateSelectionEditor';
import PlateTypeEditor from 'client/app/components/Parameters/PlateType/PlateTypeEditor';
import { ScreenRegistry } from 'client/app/registry';
import { useSnackbarManager } from 'common/ui/components/SnackbarManager';
import { logEvent } from 'common/ui/GoogleAnalyticsUtils';

export default function PlateSettings() {
  const {
    uniquePlateNames,
    setPlatesByName,
    platesByName,
    allPlatesByType: plates,
    plateMinSizeByName,
    isReadonly,
  } = useCherryPickContext();

  return (
    <Paper>
      <CardHeader title="Select plate type" />
      <CardContent>
        <DialogContentText gutterBottom>
          Please select a plate type for each plate you defined in your CherryPick
        </DialogContentText>
        {uniquePlateNames.length > 0 ? (
          <Table>
            <TableBody>
              {uniquePlateNames.map(plateName => (
                <PlateRow
                  key={plateName}
                  plateName={plateName}
                  onPlateChange={setPlatesByName}
                  platesByName={platesByName}
                  plateTypeByTypeName={plates}
                  plateMinSizeByName={plateMinSizeByName}
                  isReadonly={isReadonly}
                />
              ))}
            </TableBody>
          </Table>
        ) : (
          <Typography variant="body1">No plates specified in this CherryPick.</Typography>
        )}
      </CardContent>
    </Paper>
  );
}

type RowProps = {
  plateName: string;
  onPlateChange: SetPlatesByName;
  platesByName: PlatesByName;
  plateTypeByTypeName: PlateTypeByTypeName;
  plateMinSizeByName: PlateMinSizeByName;
  isReadonly: boolean;
};

const PlateRow = React.memo((props: RowProps) => {
  const {
    plateName,
    onPlateChange,
    platesByName,
    plateTypeByTypeName,
    plateMinSizeByName,
    isReadonly,
  } = props;
  const snackbarManager = useSnackbarManager();

  const handlePlateChange = useCallback(
    (plateType: PlateSelection) => {
      logEvent('settings-change-plate', ScreenRegistry.CHERRY_PICKER);
      const unselectingPlate = typeof plateType === 'undefined';
      if (typeof plateType === 'string') {
        const plate = plateTypeByTypeName(plateType);
        if (!plate) {
          snackbarManager.showWarning(`plate: '${plateType}' has been deleted`);
          return;
        }

        const error = isPlateBigEnough(plate, plateName, plateMinSizeByName);
        // Prevent selecting the plate if we know it won't simulate
        if (error) {
          snackbarManager.showError(error);
          return;
        }

        onPlateChange(prev => ({
          ...prev,
          [plateName]: plate,
        }));
      } else if (unselectingPlate) {
        // Delete the property rather than keep it with a value of undefined
        onPlateChange(oldValues => {
          const newValues = { ...oldValues };
          delete newValues[plateName];
          return newValues;
        });
      }
    },
    [onPlateChange, plateMinSizeByName, plateName, plateTypeByTypeName, snackbarManager],
  );

  const selectedPlate = useMemo(() => platesByName[plateName], [plateName, platesByName]);

  const plate = plateTypeByTypeName(selectedPlate?.type);
  const isPlateMissing = !plate;
  const displayName = useMemo(
    () => (plate ? plate.name : selectedPlate ? selectedPlate.type : ''),
    [selectedPlate, plate],
  );

  return (
    <TableRow>
      <TableCell>{plateName}</TableCell>
      <TableCell>
        <PlateTypeEditor
          selectedPlateType={selectedPlate?.type}
          plateTypeByTypeName={plateTypeByTypeName}
          onChange={handlePlateChange}
          displayValue={displayName}
          isPlateMissing={isPlateMissing}
          isDisabled={isReadonly}
        />
      </TableCell>
    </TableRow>
  );
});
